import { z } from "zod";
import { slugSchema } from "./slug";
import { subscriberEmailSchema } from "./subscriber";

/**
 * One line of a storefront checkout request. The cart is keyed by the product's
 * public slug (already exposed in its URL); the server resolves the slug to the
 * product and reads price/name/SKU from our own records — the client never sends
 * prices.
 */
export const checkoutItemSchema = z
  .object({
    slug: slugSchema,
    quantity: z.number().int().positive().max(999),
  })
  .strict();
export type CheckoutItem = z.infer<typeof checkoutItemSchema>;

/** Body of `POST /checkout`: the cart contents to turn into a Stripe session. */
export const createCheckoutInputSchema = z
  .object({
    items: z.array(checkoutItemSchema).min(1).max(100),
    /** Opt into the mailing list (captured by our own cart-page form). */
    subscribe: z.boolean().optional().default(false),
    /** Mailing-list email; only meaningful (and required) when `subscribe` is true. */
    email: subscriberEmailSchema.optional(),
  })
  .strict()
  .refine((v) => !v.subscribe || !!v.email, {
    message: "An email is required to join the mailing list",
    path: ["email"],
  });
export type CreateCheckoutInput = z.infer<typeof createCheckoutInputSchema>;

/** Success response: the Stripe-hosted Checkout URL to redirect the buyer to. */
export const checkoutSessionResponseSchema = z
  .object({
    url: z.string().url(),
  })
  .strict();
export type CheckoutSessionResponse = z.infer<typeof checkoutSessionResponseSchema>;
